import {deleteLocalStorage, getLang, getLocalStorage} from "./storage";
import {LoginProps} from "../types/authority";
import {notificationService} from "../services/notification";
import {IsVisitor} from "./author";
import {clearRegistrationAttribution, setSharedLoginState} from "./registrationAttribution";

type RequestRedirect = 'follow' | 'error' | 'manual';

const BASE_URL = `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_API_PREFIX}` ?? ''

interface RequestOptions extends RequestInit {
    headers?: Headers;
}

type ResponseData = string | ArrayBuffer | object | null;

export const getToken = () => {
    const useInfoStr = getLocalStorage('userInfo');
    const userInfo = useInfoStr ? JSON.parse(useInfoStr) as LoginProps : null;
    if (userInfo) return userInfo.token;
    return '';
}

const getUserName = () => {
    const useInfoStr = getLocalStorage('userInfo');
    const userInfo = useInfoStr ? JSON.parse(useInfoStr) as LoginProps : null;
    if (userInfo) return encodeURIComponent(userInfo.userName);
    return '';
}

// 定义拦截器类型
interface Interceptor {
    onRequest?: (config: RequestInit) => RequestInit | Promise<RequestInit>;
    onResponse?: (response: { response: Response; data: any }) => { response: Response } | Promise<{ response: Response }>;
    onError?: (error: any) => any;
}

class RequestInterceptor {
    private static instance: RequestInterceptor;
    private requestInterceptors: Interceptor[] = [];
    private responseInterceptors: Interceptor[] = [];

    private constructor() {}

    static getInstance(): RequestInterceptor {
        if (!RequestInterceptor.instance) {
            RequestInterceptor.instance = new RequestInterceptor();
        }
        return RequestInterceptor.instance;
    }

    use(interceptor: Interceptor) {
        if (interceptor.onRequest) {
            this.requestInterceptors.push(interceptor);
        }
        if (interceptor.onResponse) {
            this.responseInterceptors.push(interceptor);
        }
    }

    async handleRequest(config: RequestInit): Promise<RequestInit> {
        let modifiedConfig = { ...config };
        for (const interceptor of this.requestInterceptors) {
            if (interceptor.onRequest) {
                modifiedConfig = await interceptor.onRequest(modifiedConfig);
            }
        }
        return modifiedConfig;
    }

    async handleResponse(response: Response): Promise<Response> {
        let modifiedResponse = response;
        // 克隆响应对象以便多次读取
        const clonedResponse = response.clone();
        
        try {
            // 获取响应内容
            const contentType = response.headers.get('content-type');
            let responseData;
            
            if (contentType?.includes('application/json')) {
                responseData = await clonedResponse.json();
            } else if (contentType?.includes('application/octet-stream') || 
                      contentType?.includes('image/jpeg') || 
                      contentType?.includes('image/png')) {
                responseData = await clonedResponse.arrayBuffer();
            } else {
                responseData = await clonedResponse.text();
            }

            // 将响应数据传递给拦截器
            for (const interceptor of this.responseInterceptors) {
                if (interceptor.onResponse) {
                    const result = await interceptor.onResponse({
                        response: modifiedResponse,
                        data: responseData
                    });
                    if (result) {
                        modifiedResponse = result.response;
                    }
                }
            }
        } catch (error) {
            console.error('Error in response interceptor:', error);
        }
        
        return modifiedResponse;
    }
}

// 获取拦截器实例
const interceptor = RequestInterceptor.getInstance();

export async function requestGet(url: string, recoverBaseUrl?: boolean): Promise<ResponseData> {
    let data = null

    const full_url = recoverBaseUrl ? url : `${BASE_URL}${url}`
    try {
        const config = {
            headers: {
                'K-Authorization': `Bearer ${getToken()}`,
                krene_user: getUserName(),
                'X-Language': getLang()
            }
        };
        
        // 应用请求拦截器
        const modifiedConfig = await interceptor.handleRequest(config);
        
        let request = await fetch(full_url, modifiedConfig)
        
        // 应用响应拦截器
        request = await interceptor.handleResponse(request);
        
        if (request.status === 404) {
            return null
        }

        const contentType = request.headers.get('content-type')
        if (contentType && contentType.includes('application/json')) {
            data = await request.json()
        } else if (
            contentType &&
            contentType.includes('application/octet-stream')
        ) {
            data = await request.arrayBuffer()
        } else if (
            contentType &&
            (contentType.includes('image/jpeg') ||
                contentType.includes('image/png'))
        ) {
            data = await request.arrayBuffer()
        } else {
            data = await request.text()
        }
    } catch (e) {
        console.warn(`issues requesting from ${full_url}`, e)
    }
    return data
}

export async function requestPost<T = any>(url: string, payload: any, recoverBaseUrl?: boolean): Promise<T | null> {
    let json = null
    const full_url = recoverBaseUrl? url : `${BASE_URL}${url}`
    try {
        const config = {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'K-Authorization': `Bearer ${getToken()}`,
                krene_user: getUserName(),
                'X-Language': getLang()
            },
            body: JSON.stringify(payload),
        }
        
        // 应用请求拦截器
        const modifiedConfig = await interceptor.handleRequest(config);
        
        let request = await fetch(full_url, modifiedConfig)
        
        // 应用响应拦截器
        request = await interceptor.handleResponse(request);
        
        if (request.status === 404) {
            return null
        }

        json = await request.json()

    } catch (e) {
        console.warn(`issues requesting from ${full_url}`, e)
    }
    return json
}


export async function requestDelete<T = any>(url: string, recoverBaseUrl?: boolean): Promise<T | null> {
    let json = null
    const full_url = recoverBaseUrl ? url : `${BASE_URL}${url}`
    try {
        const config = {
            method: 'delete',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'K-Authorization': `Bearer ${getToken()}`,
                krene_user: getUserName(),
                'X-Language': getLang()
            }
        }

        // 应用请求拦截器
        const modifiedConfig = await interceptor.handleRequest(config);

        let request = await fetch(full_url, modifiedConfig)

        // 应用响应拦截器
        request = await interceptor.handleResponse(request);

        if (request.status === 404) {
            return null
        }

        json = await request.json()

    } catch (e) {
        console.warn(`issues requesting from ${full_url}`, e)
    }
    return json
}


interface FormDataPayload {
    [key: string]: string | Blob;
}

export async function requestFormDataPost<T = any>(url: string, payload: FormData): Promise<T | null> {
    const full_url = `${BASE_URL}${url}`
    try {
        const myHeaders = new Headers()
        myHeaders.append('Cookie', 'PHPSESSID=n70fa2vmvm6tfmktf4jmstmd1i')

        const formdata = payload

        const headers = {
            Accept: 'application/json, text/plain, */*',
            'K-Authorization': `Bearer ${getToken()}`,
            krene_user: getUserName(),
            'X-Language': getLang()
        }

        const requestOptions = {
            method: 'POST',
            headers: headers,
            body: formdata,
            redirect: 'follow' as RequestRedirect,
        }

        const response = await fetch(full_url, requestOptions)
        return response.json()
    } catch (e) {
        console.warn(e)
        return null;
    }
}

interface ScriptsResponse {
    txt2img?: string[];
    img2img?: string[];
}

export async function postArrayBuffer<T = any>(url: string, data: ArrayBuffer | Uint8Array): Promise<T> {
    const response = await fetch(url, {
        method: 'POST',
        body: data,
        headers: {
            'Content-Type': 'application/octet-stream', // or whatever type your data should be
        },
    })

    if (!response.ok) {
        throw new Error('Network response was not ok')
    }

    return response.json() // parses JSON response into native JavaScript objects
}

export async function requestPut<T = any>(url: string, payload: any, recoverBaseUrl?: boolean): Promise<T | null> {
    let json = null
    const full_url = recoverBaseUrl ? url : `${BASE_URL}${url}`
    try {
        let request = await fetch(full_url, {
            method: 'PUT',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'K-Authorization': `Bearer ${getToken()}`,
                krene_user: getUserName(),
                'X-Language': getLang()
            },
            body: JSON.stringify(payload),
        })

        if (request.status === 404) {
            return null
        }

        json = await request.json()

    } catch (e) {
        console.warn(`issues requesting from ${full_url}`, e)
    }
    return json
}

interceptor.use({
    onRequest: (config) => {
        console.log('Request config:', config);
        return config;
    },
    onResponse: ({ response, data }) => {

        if (data?.code === 401 && !IsVisitor()) {
            deleteLocalStorage('userInfo')
            clearRegistrationAttribution()
            setSharedLoginState(false)
            notificationService.show('登录已过期，请重新登录', 'warning');
            setTimeout(() => {
                window.location.href = `${process.env.PUBLIC_URL}/dashboard/home`;
            }, 1500);
        }

        if (response.status === 401) {
            // 处理未授权
        }
        
        return { response };
    }
});
